/**
 * InvestorProgressVisibilityNotice
 *
 * Symmetric-transparency notice shown to the COMPANY (CTO/founder) in their own
 * report view, next to the investor-visibility controls. It tells the company
 * exactly what linked investors can observe while a deal is in flight: their
 * PROGRESS as coarse milestones only — never document contents, answers, or
 * activity timestamps beyond phase changes.
 *
 * The milestone list is sourced from {@link MILESTONE_SEQUENCE} /
 * {@link AssessmentMilestoneLabels} in `@app/types` so it can never drift from
 * the stepper investors actually see (ship-gate sv-0xt: this must ship in the
 * same release as investor milestone visibility).
 */

import { AssessmentMilestoneLabels, MILESTONE_SEQUENCE } from "@app/types";
import { usePortfolioLinks } from "@/hooks/useInvestorAccess";
import { Eye } from "lucide-react";

/**
 * Company-facing "what investors can see" notice.
 *
 * Self-gating: renders only when the company actually has linked investor
 * funds (reuses the {@link usePortfolioLinks} has-linked-funds pattern from
 * InvestorVisibilityToggle). When there is no investor exposure there is
 * nothing to disclose, so the component renders `null`.
 *
 * @returns The visibility notice card, or `null` when no investor funds are linked.
 */
export function InvestorProgressVisibilityNotice(): JSX.Element | null {
  const { data: links } = usePortfolioLinks();

  // Fail OPEN: only hide when we have definitive data showing zero linked funds.
  // On a loading/errored query `links` is undefined — suppressing a
  // privacy-relevant disclosure on a transient fetch failure is worse than
  // showing a generic, always-true notice, so we render it in that case.
  if (links?.length === 0) return null;

  return (
    <div
      data-testid="investor-progress-visibility-notice"
      className="rounded-md bg-muted/50 px-3 py-2.5 text-xs text-muted-foreground"
    >
      <div className="flex items-start gap-2">
        <Eye className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div className="space-y-1.5">
          <p className="font-semibold text-foreground">
            What investors can see
          </p>
          <p>
            Investors who requested this assessment can follow your progress as
            coarse milestones:
          </p>
          <ul className="space-y-0.5">
            {MILESTONE_SEQUENCE.map((milestone) => (
              <li key={milestone} className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="h-1 w-1 rounded-full bg-current flex-shrink-0"
                />
                {AssessmentMilestoneLabels[milestone]}
              </li>
            ))}
          </ul>
          <p>
            They can never see your uploaded documents, your answers, or any
            activity beyond these phase changes. You see exactly what they see.
          </p>
        </div>
      </div>
    </div>
  );
}
